import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon, SendIcon, RotateCcwIcon, DownloadIcon, SmartphoneIcon, MonitorIcon, ZoomInIcon, ZoomOutIcon, CheckIcon, WandIcon, Maximize2Icon, ChevronDownIcon, LayoutIcon, FileTextIcon, SettingsIcon, TagIcon, CpuIcon, TypeIcon, PenLineIcon, ListIcon, UsersIcon, TargetIcon, MessageSquareIcon, BrainIcon, EyeIcon, BoxIcon } from 'lucide-react';
type DesignStyle = 'modern-soft' | 'material' | 'brutalist' | 'retro-terminal' | 'glass' | 'minimalist';
type CreatorTab = 'designer' | 'details';
type PromptMode = 'freeform' | 'guided';
type ColorPalette = {
  id: string;
  name: string;
  primary: string;
  secondary: string;
  accent: string;
};
type GuidedPrompt = {
  purpose: string;
  targetUsers: string;
  interfaceLayout: string;
  keyFeatures: string;
  aiBehavior: string;
  exampleInteraction: string;
};
// GuidedInput component defined OUTSIDE Create to prevent re-renders
const GuidedInput = ({
  icon: Icon,
  label,
  placeholder,
  value,
  onChange,
  multiline = false,
  accentColor
}: {
  icon: BoxIcon;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  accentColor: string;
}) => <div className="space-y-1.5" data-id="element-1382">
    <label className="flex items-center space-x-2 text-xs font-medium text-gray-600" data-id="element-1383">
      <Icon className="h-3.5 w-3.5" data-id="element-1384" />
      <span data-id="element-1385">{label}</span>
    </label>
    {multiline ? <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent" style={{
    '--tw-ring-color': accentColor
  } as React.CSSProperties} data-id="element-1386" /> : <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent" style={{
    '--tw-ring-color': accentColor
  } as React.CSSProperties} data-id="element-1387" />}
  </div>;
const COLOR_PALETTES: ColorPalette[] = [{
  id: 'ocean-blue',
  name: 'Ocean Blue',
  primary: '#2563eb',
  secondary: '#06b6d4',
  accent: '#14b8a6'
}, {
  id: 'forest-green',
  name: 'Forest Green',
  primary: '#16a34a',
  secondary: '#10b981',
  accent: '#84cc16'
}, {
  id: 'sunset-orange',
  name: 'Sunset Orange',
  primary: '#ea580c',
  secondary: '#ef4444',
  accent: '#f59e0b'
}, {
  id: 'royal-purple',
  name: 'Royal Purple',
  primary: '#9333ea',
  secondary: '#6366f1',
  accent: '#a855f7'
}, {
  id: 'slate-gray',
  name: 'Slate Gray',
  primary: '#334155',
  secondary: '#475569',
  accent: '#71717a'
}, {
  id: 'rose-pink',
  name: 'Rose Pink',
  primary: '#e11d48',
  secondary: '#ec4899',
  accent: '#d946ef'
}, {
  id: 'amber-gold',
  name: 'Amber Gold',
  primary: '#d97706',
  secondary: '#eab308',
  accent: '#f97316'
}, {
  id: 'emerald-mint',
  name: 'Emerald Mint',
  primary: '#059669',
  secondary: '#4ade80',
  accent: '#2dd4bf'
}];
type StyleTemplate = {
  id: DesignStyle;
  name: string;
  description: string;
};
const STYLE_TEMPLATES: StyleTemplate[] = [{
  id: 'modern-soft',
  name: 'Modern Soft',
  description: 'Rounded, gentle, approachable'
}, {
  id: 'material',
  name: 'Material Design',
  description: 'Bold, elevated, structured'
}, {
  id: 'brutalist',
  name: 'Brutalist',
  description: 'Bold, raw, high-contrast'
}, {
  id: 'retro-terminal',
  name: 'Retro Terminal',
  description: 'Nostalgic, monospace, vintage'
}, {
  id: 'glass',
  name: 'Glass Morphism',
  description: 'Frosted, transparent, modern'
}, {
  id: 'minimalist',
  name: 'Minimalist',
  description: 'Clean, spacious, understated'
}];
const AI_MODELS = [{
  id: 'gpt-4',
  name: 'GPT-4',
  provider: 'OpenAI'
}, {
  id: 'gpt-3.5',
  name: 'GPT-3.5 Turbo',
  provider: 'OpenAI'
}, {
  id: 'claude-3',
  name: 'Claude 3',
  provider: 'Anthropic'
}, {
  id: 'gemini-pro',
  name: 'Gemini Pro',
  provider: 'Google'
}, {
  id: 'llama-2',
  name: 'Llama 2',
  provider: 'Meta'
}];
const CATEGORIES = ['Productivity', 'Image Generation', 'Code & Development', 'Writing', 'Education', 'Business', 'Entertainment', 'Lifestyle', 'Other'];
export const Create = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<CreatorTab>('designer');
  const [promptMode, setPromptMode] = useState<PromptMode>('guided');
  const [selectedStyle, setSelectedStyle] = useState<DesignStyle>('modern-soft');
  const [selectedPalette, setSelectedPalette] = useState<string>('ocean-blue');
  const [freeformPrompt, setFreeformPrompt] = useState('');
  const [guidedPrompt, setGuidedPrompt] = useState<GuidedPrompt>({
    purpose: '',
    targetUsers: '',
    interfaceLayout: '',
    keyFeatures: '',
    aiBehavior: '',
    exampleInteraction: ''
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [zoomLevel, setZoomLevel] = useState(100);
  const [generatedPreview, setGeneratedPreview] = useState<string | null>(null);
  const [iterations, setIterations] = useState<string[]>([]);
  const [showGeneratedPrompt, setShowGeneratedPrompt] = useState(false);
  const [showStyleDropdown, setShowStyleDropdown] = useState(false);
  const [showPaletteDropdown, setShowPaletteDropdown] = useState(false);
  const [appDetails, setAppDetails] = useState({
    name: '',
    description: '',
    category: 'Productivity',
    aiModel: 'gpt-4',
    icon: 'sparkles',
    isPublic: true
  });
  const styleDropdownRef = useRef<HTMLDivElement>(null);
  const paletteDropdownRef = useRef<HTMLDivElement>(null);
  const currentTemplate = STYLE_TEMPLATES.find(t => t.id === selectedStyle)!;
  const currentPalette = COLOR_PALETTES.find(p => p.id === selectedPalette)!;
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (styleDropdownRef.current && !styleDropdownRef.current.contains(e.target as Node)) {
        setShowStyleDropdown(false);
      }
      if (paletteDropdownRef.current && !paletteDropdownRef.current.contains(e.target as Node)) {
        setShowPaletteDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const generateCombinedPrompt = (): string => {
    const parts: string[] = [];
    if (guidedPrompt.purpose) parts.push(`Create an app that ${guidedPrompt.purpose}`);
    if (guidedPrompt.targetUsers) parts.push(`This app is designed for ${guidedPrompt.targetUsers}`);
    if (guidedPrompt.interfaceLayout) parts.push(`Interface: ${guidedPrompt.interfaceLayout}`);
    if (guidedPrompt.keyFeatures) parts.push(`Key features: ${guidedPrompt.keyFeatures}`);
    if (guidedPrompt.aiBehavior) parts.push(`AI behavior: ${guidedPrompt.aiBehavior}`);
    if (guidedPrompt.exampleInteraction) parts.push(`Example interaction: ${guidedPrompt.exampleInteraction}`);
    return parts.join('. ');
  };
  const getCurrentPrompt = (): string => {
    return promptMode === 'freeform' ? freeformPrompt : generateCombinedPrompt();
  };
  const hasPromptContent = (): boolean => {
    if (promptMode === 'freeform') return freeformPrompt.trim().length > 0;
    return Object.values(guidedPrompt).some(v => v.trim().length > 0);
  };
  const handleGenerate = async () => {
    if (!hasPromptContent() || isGenerating) return;
    setIsGenerating(true);
    const currentPromptText = getCurrentPrompt();
    setIterations([currentPromptText, ...iterations]);
    setTimeout(() => {
      setGeneratedPreview('generated');
      setIsGenerating(false);
    }, 2000);
  };
  const handleReset = () => {
    setGeneratedPreview(null);
    setFreeformPrompt('');
    setGuidedPrompt({
      purpose: '',
      targetUsers: '',
      interfaceLayout: '',
      keyFeatures: '',
      aiBehavior: '',
      exampleInteraction: ''
    });
    setIterations([]);
  };
  const updateGuidedField = (field: keyof GuidedPrompt) => (value: string) => {
    setGuidedPrompt(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const getPreviewContent = () => {
    if (!generatedPreview) {
      return <div className="flex items-center justify-center h-full" data-id="element-1388">
          <div className="text-center max-w-md px-6" data-id="element-1389">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{
            background: `linear-gradient(135deg, ${currentPalette.primary}, ${currentPalette.secondary})`
          }} data-id="element-1390">
              <WandIcon className="h-10 w-10 text-white" data-id="element-1391" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1392">
              Ready to Create
            </h3>
            <p className="text-gray-500 text-sm" data-id="element-1393">
              Describe your app and watch it come to life
            </p>
          </div>
        </div>;
    }
    const getStyleClasses = () => {
      switch (selectedStyle) {
        case 'modern-soft':
          return 'rounded-2xl shadow-xl border';
        case 'material':
          return 'rounded-lg shadow-2xl bg-white border border-gray-200';
        case 'brutalist':
          return 'rounded-none border-4 border-black bg-white';
        case 'retro-terminal':
          return 'rounded-none bg-black shadow-lg';
        case 'glass':
          return 'rounded-3xl bg-white/20 backdrop-blur-xl border border-white/30 shadow-2xl';
        case 'minimalist':
          return 'rounded-sm bg-white border border-gray-200 shadow-sm';
        default:
          return '';
      }
    };
    const getBackgroundStyle = () => {
      if (selectedStyle === 'modern-soft') {
        return {
          background: `linear-gradient(135deg, ${currentPalette.primary}15, ${currentPalette.secondary}15)`
        };
      }
      if (selectedStyle === 'retro-terminal') {
        return {
          backgroundColor: '#000',
          borderColor: currentPalette.primary,
          borderWidth: '2px'
        };
      }
      return {};
    };
    const getButtonStyle = () => {
      if (selectedStyle === 'brutalist') return {
        backgroundColor: currentPalette.accent,
        color: '#000',
        border: '4px solid #000'
      };
      if (selectedStyle === 'retro-terminal') return {
        backgroundColor: currentPalette.primary,
        color: '#000',
        fontFamily: 'monospace'
      };
      if (selectedStyle === 'glass') return {
        backgroundColor: `${currentPalette.primary}30`,
        backdropFilter: 'blur(12px)',
        color: '#1f2937'
      };
      return {
        background: `linear-gradient(135deg, ${currentPalette.primary}, ${currentPalette.secondary})`,
        color: '#fff'
      };
    };
    const getTextColor = () => selectedStyle === 'retro-terminal' ? {
      color: currentPalette.primary
    } : {
      color: '#1f2937'
    };
    return <div className={`${getStyleClasses()} p-6 max-w-xl mx-auto`} style={getBackgroundStyle()} data-id="element-1394">
        <h2 className="text-2xl font-bold mb-3" style={getTextColor()} data-id="element-1395">
          {appDetails.name || guidedPrompt.purpose?.split(' ').slice(0, 3).join(' ') || 'Your App'}
        </h2>
        <p className="mb-5 opacity-80 text-sm" style={getTextColor()} data-id="element-1396">
          Preview with {currentTemplate.name} style
        </p>
        <div className="space-y-3" data-id="element-1397">
          <input type="text" placeholder="Enter text..." className={`w-full px-3 py-2 text-sm ${selectedStyle === 'brutalist' ? 'border-2 border-black rounded-none' : selectedStyle === 'retro-terminal' ? 'bg-black font-mono rounded-none' : 'border border-gray-300 rounded-lg'}`} style={selectedStyle === 'retro-terminal' ? {
          borderColor: currentPalette.primary,
          color: currentPalette.primary
        } : {}} data-id="element-1398" />
          <button className={`w-full px-4 py-2.5 font-medium text-sm ${selectedStyle === 'brutalist' || selectedStyle === 'retro-terminal' ? 'rounded-none' : selectedStyle === 'glass' ? 'rounded-xl' : 'rounded-lg'}`} style={getButtonStyle()} data-id="element-1399">
            Primary Action
          </button>
        </div>
      </div>;
  };
  return <div className="h-screen flex flex-col bg-gray-50" data-id="element-1400">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between" data-id="element-1401">
        <div className="flex items-center space-x-6" data-id="element-1402">
          <div className="flex items-center space-x-3" data-id="element-1403">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
            background: `linear-gradient(135deg, ${currentPalette.primary}, ${currentPalette.secondary})`
          }} data-id="element-1404">
              <SparklesIcon className="h-5 w-5 text-white" data-id="element-1405" />
            </div>
            <h1 className="text-base font-semibold text-gray-900" data-id="element-1406">
              Create App
            </h1>
          </div>

          <div className="flex bg-gray-100 rounded-lg p-1" data-id="element-1407">
            <button onClick={() => setActiveTab('designer')} className={`flex items-center space-x-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'designer' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1408">
              <LayoutIcon className="h-4 w-4" data-id="element-1409" />
              <span data-id="element-1410">Designer</span>
            </button>
            <button onClick={() => setActiveTab('details')} className={`flex items-center space-x-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'details' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1411">
              <FileTextIcon className="h-4 w-4" data-id="element-1412" />
              <span data-id="element-1413">App Details</span>
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-3" data-id="element-1414">
          <button onClick={() => navigate('/app-store-listing')} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium" data-id="element-1415">
            Preview Listing
          </button>
          <button className="px-5 py-2 text-white rounded-lg transition-colors text-sm font-medium shadow-sm" style={{
          backgroundColor: currentPalette.primary
        }} data-id="element-1416">
            Publish
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden" data-id="element-1417">
        {activeTab === 'designer' ? <>
            {/* Left Panel - Designer Controls */}
            <div className="w-[400px] bg-white border-r border-gray-200 flex flex-col overflow-hidden" data-id="element-1418">
              {/* Compact Selectors */}
              <div className="p-4 border-b border-gray-200 space-y-3 flex-shrink-0" data-id="element-1419">
                {/* Style Dropdown */}
                <div ref={styleDropdownRef} className="relative" data-id="element-1420">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-1421">
                    Design Style
                  </label>
                  <button onClick={() => {
                setShowStyleDropdown(!showStyleDropdown);
                setShowPaletteDropdown(false);
              }} className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1422">
                    <div className="flex items-center space-x-2" data-id="element-1423">
                      <LayoutIcon className="h-4 w-4 text-gray-400" data-id="element-1424" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-1425">
                        {currentTemplate.name}
                      </span>
                    </div>
                    <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${showStyleDropdown ? 'rotate-180' : ''}`} data-id="element-1426" />
                  </button>

                  {showStyleDropdown && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto" data-id="element-1427">
                      {STYLE_TEMPLATES.map(style => <button key={style.id} onClick={() => {
                  setSelectedStyle(style.id);
                  setShowStyleDropdown(false);
                }} className={`w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors ${selectedStyle === style.id ? 'bg-gray-50' : ''}`} data-id="element-1428">
                          <div data-id="element-1429">
                            <div className="text-sm font-medium text-gray-900" data-id="element-1430">
                              {style.name}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-1431">
                              {style.description}
                            </div>
                          </div>
                          {selectedStyle === style.id && <CheckIcon className="h-4 w-4" style={{
                    color: currentPalette.primary
                  }} data-id="element-1432" />}
                        </button>)}
                    </div>}
                </div>

                {/* Palette Dropdown */}
                <div ref={paletteDropdownRef} className="relative" data-id="element-1433">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-1434">
                    Color Palette
                  </label>
                  <button onClick={() => {
                setShowPaletteDropdown(!showPaletteDropdown);
                setShowStyleDropdown(false);
              }} className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1435">
                    <div className="flex items-center space-x-3" data-id="element-1436">
                      <div className="flex space-x-1" data-id="element-1437">
                        <div className="w-4 h-4 rounded" style={{
                      backgroundColor: currentPalette.primary
                    }} data-id="element-1438" />
                        <div className="w-4 h-4 rounded" style={{
                      backgroundColor: currentPalette.secondary
                    }} data-id="element-1439" />
                        <div className="w-4 h-4 rounded" style={{
                      backgroundColor: currentPalette.accent
                    }} data-id="element-1440" />
                      </div>
                      <span className="text-sm font-medium text-gray-900" data-id="element-1441">
                        {currentPalette.name}
                      </span>
                    </div>
                    <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${showPaletteDropdown ? 'rotate-180' : ''}`} data-id="element-1442" />
                  </button>

                  {showPaletteDropdown && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto" data-id="element-1443">
                      {COLOR_PALETTES.map(palette => <button key={palette.id} onClick={() => {
                  setSelectedPalette(palette.id);
                  setShowPaletteDropdown(false);
                }} className={`w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors ${selectedPalette === palette.id ? 'bg-gray-50' : ''}`} data-id="element-1444">
                          <div className="flex items-center space-x-3" data-id="element-1445">
                            <div className="flex space-x-1" data-id="element-1446">
                              <div className="w-4 h-4 rounded" style={{
                        backgroundColor: palette.primary
                      }} data-id="element-1447" />
                              <div className="w-4 h-4 rounded" style={{
                        backgroundColor: palette.secondary
                      }} data-id="element-1448" />
                              <div className="w-4 h-4 rounded" style={{
                        backgroundColor: palette.accent
                      }} data-id="element-1449" />
                            </div>
                            <span className="text-sm font-medium text-gray-900" data-id="element-1450">
                              {palette.name}
                            </span>
                          </div>
                          {selectedPalette === palette.id && <CheckIcon className="h-4 w-4" style={{
                    color: palette.primary
                  }} data-id="element-1451" />}
                        </button>)}
                    </div>}
                </div>
              </div>

              {/* Prompt Mode Toggle */}
              <div className="px-4 pt-4 pb-2 flex-shrink-0" data-id="element-1452">
                <div className="flex bg-gray-100 rounded-lg p-0.5" data-id="element-1453">
                  <button onClick={() => setPromptMode('guided')} className={`flex-1 flex items-center justify-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${promptMode === 'guided' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1454">
                    <ListIcon className="h-3.5 w-3.5" data-id="element-1455" />
                    <span data-id="element-1456">Guided</span>
                  </button>
                  <button onClick={() => setPromptMode('freeform')} className={`flex-1 flex items-center justify-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${promptMode === 'freeform' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1457">
                    <PenLineIcon className="h-3.5 w-3.5" data-id="element-1458" />
                    <span data-id="element-1459">Freeform</span>
                  </button>
                </div>
              </div>

              {/* Prompt Area */}
              <div className="flex-1 overflow-y-auto p-4" data-id="element-1460">
                {promptMode === 'freeform' ? <div className="h-full flex flex-col" data-id="element-1461">
                    <div className="flex items-center justify-between mb-2" data-id="element-1462">
                      <label className="text-xs font-medium text-gray-500" data-id="element-1463">
                        Describe Your App
                      </label>
                      {freeformPrompt && <button onClick={() => setFreeformPrompt('')} className="text-xs text-gray-400 hover:text-gray-600" data-id="element-1464">
                          Clear
                        </button>}
                    </div>
                    <textarea value={freeformPrompt} onChange={e => setFreeformPrompt(e.target.value)} placeholder="Create a task management app with a clean interface. Include a list of tasks, add task button, and mark complete functionality. The AI should be helpful and encouraging..." className="flex-1 w-full border border-gray-200 rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-offset-0 text-sm" style={{
                minHeight: '200px'
              }} data-id="element-1465" />
                  </div> : <div className="space-y-4" data-id="element-1466">
                    <GuidedInput icon={TargetIcon} label="What does your app do?" placeholder="Helps users manage their daily tasks and stay organized..." value={guidedPrompt.purpose} onChange={updateGuidedField('purpose')} multiline accentColor={currentPalette.primary} data-id="element-1467" />

                    <GuidedInput icon={UsersIcon} label="Who is this app for?" placeholder="Busy professionals, students, anyone who wants to stay organized..." value={guidedPrompt.targetUsers} onChange={updateGuidedField('targetUsers')} accentColor={currentPalette.primary} data-id="element-1468" />

                    <GuidedInput icon={LayoutIcon} label="How should it look?" placeholder="Clean sidebar navigation, main content area with task list, floating add button..." value={guidedPrompt.interfaceLayout} onChange={updateGuidedField('interfaceLayout')} multiline accentColor={currentPalette.primary} data-id="element-1469" />

                    <GuidedInput icon={ListIcon} label="What are the key features?" placeholder="Add/edit/delete tasks, due dates, priority levels, categories, search..." value={guidedPrompt.keyFeatures} onChange={updateGuidedField('keyFeatures')} multiline accentColor={currentPalette.primary} data-id="element-1470" />

                    <GuidedInput icon={BrainIcon} label="How should the AI behave?" placeholder="Be encouraging and supportive, suggest task breakdowns, remind about deadlines gently..." value={guidedPrompt.aiBehavior} onChange={updateGuidedField('aiBehavior')} multiline accentColor={currentPalette.primary} data-id="element-1471" />

                    <GuidedInput icon={MessageSquareIcon} label="Example interaction (optional)" placeholder="User: 'I need to finish my report' → AI: 'Let's break that down into smaller steps...'" value={guidedPrompt.exampleInteraction} onChange={updateGuidedField('exampleInteraction')} multiline accentColor={currentPalette.primary} data-id="element-1472" />

                    {hasPromptContent() && <div className="pt-2" data-id="element-1473">
                        <button onClick={() => setShowGeneratedPrompt(!showGeneratedPrompt)} className="flex items-center space-x-1.5 text-xs font-medium text-gray-500 hover:text-gray-700" data-id="element-1474">
                          <EyeIcon className="h-3.5 w-3.5" data-id="element-1475" />
                          <span data-id="element-1476">
                            {showGeneratedPrompt ? 'Hide' : 'Preview'} generated
                            prompt
                          </span>
                        </button>
                        {showGeneratedPrompt && <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs text-gray-600" data-id="element-1477">
                            {generateCombinedPrompt()}
                          </div>}
                      </div>}
                  </div>}
              </div>

              {/* Generate Button */}
              <div className="p-4 border-t border-gray-200 flex-shrink-0" data-id="element-1478">
                <div className="flex space-x-2" data-id="element-1479">
                  <button onClick={handleGenerate} disabled={!hasPromptContent() || isGenerating} className="flex-1 text-white px-4 py-2.5 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2 text-sm" style={{
                backgroundColor: currentPalette.primary
              }} data-id="element-1480">
                    {isGenerating ? <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" data-id="element-1481" />
                        <span data-id="element-1482">Generating...</span>
                      </> : <>
                        <SendIcon className="h-4 w-4" data-id="element-1483" />
                        <span data-id="element-1484">Generate</span>
                      </>}
                  </button>
                  <button onClick={handleReset} className="px-3 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1485">
                    <RotateCcwIcon className="h-4 w-4 text-gray-500" data-id="element-1486" />
                  </button>
                </div>

                {iterations.length > 0 && <div className="mt-3" data-id="element-1487">
                    <label className="text-xs font-medium text-gray-500 mb-1.5 block" data-id="element-1488">
                      History
                    </label>
                    <div className="space-y-1 max-h-20 overflow-y-auto" data-id="element-1489">
                      {iterations.slice(0, 3).map((iter, idx) => <button key={idx} onClick={() => {
                  if (promptMode === 'freeform') setFreeformPrompt(iter);
                }} className="w-full text-left px-2 py-1.5 bg-gray-50 hover:bg-gray-100 rounded text-xs text-gray-600 line-clamp-1" data-id="element-1490">
                          {iter}
                        </button>)}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Right Panel - Preview */}
            <div className="flex-1 flex flex-col bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-1491">
              <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between" data-id="element-1492">
                <div className="flex items-center space-x-1" data-id="element-1493">
                  <button onClick={() => setPreviewDevice('desktop')} className={`p-1.5 rounded transition-colors ${previewDevice === 'desktop' ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:bg-gray-50'}`} data-id="element-1494">
                    <MonitorIcon className="h-4 w-4" data-id="element-1495" />
                  </button>
                  <button onClick={() => setPreviewDevice('mobile')} className={`p-1.5 rounded transition-colors ${previewDevice === 'mobile' ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:bg-gray-50'}`} data-id="element-1496">
                    <SmartphoneIcon className="h-4 w-4" data-id="element-1497" />
                  </button>
                  <div className="w-px h-5 bg-gray-200 mx-2" data-id="element-1498" />
                  <button onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))} className="p-1.5 rounded text-gray-400 hover:bg-gray-50" data-id="element-1499">
                    <ZoomOutIcon className="h-4 w-4" data-id="element-1500" />
                  </button>
                  <span className="text-xs text-gray-500 font-medium w-10 text-center" data-id="element-1501">
                    {zoomLevel}%
                  </span>
                  <button onClick={() => setZoomLevel(Math.min(150, zoomLevel + 10))} className="p-1.5 rounded text-gray-400 hover:bg-gray-50" data-id="element-1502">
                    <ZoomInIcon className="h-4 w-4" data-id="element-1503" />
                  </button>
                </div>
                <div className="flex items-center space-x-1" data-id="element-1504">
                  <button className="p-1.5 rounded text-gray-400 hover:bg-gray-50" data-id="element-1505">
                    <Maximize2Icon className="h-4 w-4" data-id="element-1506" />
                  </button>
                  <button className="p-1.5 rounded text-gray-400 hover:bg-gray-50" data-id="element-1507">
                    <DownloadIcon className="h-4 w-4" data-id="element-1508" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto p-6 flex items-center justify-center" data-id="element-1509">
                <div className={`transition-all duration-300 ${previewDevice === 'mobile' ? 'max-w-xs' : 'w-full max-w-4xl'}`} style={{
              transform: `scale(${zoomLevel / 100})`
            }} data-id="element-1510">
                  {isGenerating ? <div className="flex items-center justify-center h-80" data-id="element-1511">
                      <div className="text-center" data-id="element-1512">
                        <div className="animate-spin rounded-full h-10 w-10 border-3 border-t-transparent mx-auto mb-3" style={{
                    borderColor: currentPalette.primary,
                    borderTopColor: 'transparent'
                  }} data-id="element-1513" />
                        <p className="text-gray-500 text-sm" data-id="element-1514">Generating...</p>
                      </div>
                    </div> : getPreviewContent()}
                </div>
              </div>
            </div>
          </> /* App Details Tab */ : <div className="flex-1 overflow-y-auto" data-id="element-1515">
            <div className="max-w-2xl mx-auto p-8" data-id="element-1516">
              <div className="mb-8" data-id="element-1517">
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1518">
                  App Details
                </h2>
                <p className="text-gray-500" data-id="element-1519">
                  Configure your app's information for the store listing
                </p>
              </div>

              <div className="space-y-6" data-id="element-1520">
                <div data-id="element-1521">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2" data-id="element-1522">
                    <TypeIcon className="h-4 w-4" data-id="element-1523" />
                    <span data-id="element-1524">App Name</span>
                  </label>
                  <input type="text" value={appDetails.name} onChange={e => setAppDetails({
                ...appDetails,
                name: e.target.value
              })} placeholder="My Awesome App" className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-offset-0" data-id="element-1525" />
                </div>

                <div data-id="element-1526">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2" data-id="element-1527">
                    <FileTextIcon className="h-4 w-4" data-id="element-1528" />
                    <span data-id="element-1529">Description</span>
                  </label>
                  <textarea value={appDetails.description} onChange={e => setAppDetails({
                ...appDetails,
                description: e.target.value
              })} placeholder="Describe what your app does..." rows={4} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-offset-0" data-id="element-1530" />
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-1531">
                  <div data-id="element-1532">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2" data-id="element-1533">
                      <TagIcon className="h-4 w-4" data-id="element-1534" />
                      <span data-id="element-1535">Category</span>
                    </label>
                    <select value={appDetails.category} onChange={e => setAppDetails({
                  ...appDetails,
                  category: e.target.value
                })} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-offset-0 bg-white" data-id="element-1536">
                      {CATEGORIES.map(cat => <option key={cat} value={cat} data-id="element-1537">
                          {cat}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1538">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2" data-id="element-1539">
                      <CpuIcon className="h-4 w-4" data-id="element-1540" />
                      <span data-id="element-1541">AI Model</span>
                    </label>
                    <select value={appDetails.aiModel} onChange={e => setAppDetails({
                  ...appDetails,
                  aiModel: e.target.value
                })} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-offset-0 bg-white" data-id="element-1542">
                      {AI_MODELS.map(model => <option key={model.id} value={model.id} data-id="element-1543">
                          {model.name} ({model.provider})
                        </option>)}
                    </select>
                  </div>
                </div>

                <div data-id="element-1544">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-3" data-id="element-1545">
                    <SettingsIcon className="h-4 w-4" data-id="element-1546" />
                    <span data-id="element-1547">Visibility</span>
                  </label>
                  <div className="flex space-x-3" data-id="element-1548">
                    <button onClick={() => setAppDetails({
                  ...appDetails,
                  isPublic: true
                })} className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${appDetails.isPublic ? 'border-current bg-opacity-10' : 'border-gray-200 hover:border-gray-300'}`} style={appDetails.isPublic ? {
                  borderColor: currentPalette.primary,
                  backgroundColor: `${currentPalette.primary}10`
                } : {}} data-id="element-1549">
                      <div className="font-medium text-gray-900" data-id="element-1550">Public</div>
                      <div className="text-xs text-gray-500 mt-0.5" data-id="element-1551">
                        Listed in the app store
                      </div>
                    </button>
                    <button onClick={() => setAppDetails({
                  ...appDetails,
                  isPublic: false
                })} className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${!appDetails.isPublic ? 'border-current bg-opacity-10' : 'border-gray-200 hover:border-gray-300'}`} style={!appDetails.isPublic ? {
                  borderColor: currentPalette.primary,
                  backgroundColor: `${currentPalette.primary}10`
                } : {}} data-id="element-1552">
                      <div className="font-medium text-gray-900" data-id="element-1553">Private</div>
                      <div className="text-xs text-gray-500 mt-0.5" data-id="element-1554">
                        Only you can access
                      </div>
                    </button>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200" data-id="element-1555">
                  <div className="text-xs font-medium text-gray-500 mb-2" data-id="element-1556">
                    Design Configuration
                  </div>
                  <div className="flex items-center justify-between" data-id="element-1557">
                    <div className="flex items-center space-x-4" data-id="element-1558">
                      <div className="flex items-center space-x-2" data-id="element-1559">
                        <LayoutIcon className="h-4 w-4 text-gray-400" data-id="element-1560" />
                        <span className="text-sm text-gray-700" data-id="element-1561">
                          {currentTemplate.name}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2" data-id="element-1562">
                        <div className="flex space-x-1" data-id="element-1563">
                          <div className="w-4 h-4 rounded" style={{
                        backgroundColor: currentPalette.primary
                      }} data-id="element-1564" />
                          <div className="w-4 h-4 rounded" style={{
                        backgroundColor: currentPalette.secondary
                      }} data-id="element-1565" />
                          <div className="w-4 h-4 rounded" style={{
                        backgroundColor: currentPalette.accent
                      }} data-id="element-1566" />
                        </div>
                        <span className="text-sm text-gray-700" data-id="element-1567">
                          {currentPalette.name}
                        </span>
                      </div>
                    </div>
                    <button onClick={() => setActiveTab('designer')} className="text-sm font-medium hover:underline" style={{
                  color: currentPalette.primary
                }} data-id="element-1568">
                      Edit Design →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </div>;
};